/** One reward vocabulary, read by everything that hands a hero a prize.
 *
 * A creature bank, a Mystery Casket and a Oracle's Hut differ in what they ask
 * for, not in what they pay out, and before this file each of them would have
 * had to restate the payout. It is stated once here so that adding a payout
 * kind - a spell, say - reaches all three at the same time.
 *
 * Keys, all optional, all read off the object's own payload:
 *
 * | key              | effect                                             |
 * |------------------|----------------------------------------------------|
 * | `gold`           | gold to the player                                  |
 * | `resource`+`amount` | that resource to the player                      |
 * | `artifact`       | into the hero's backpack                            |
 * | `rewardCreature`+`rewardCount` | into the hero's army, if it fits      |
 * | `experience`     | to the hero, scaled by their own modifiers          |
 * | `spell`          | into the hero's spellbook, if they can learn it     |
 * | `primary`+`primaryAmount` | a permanent primary-stat rise             |
 *
 * Depends on `ctx`, `hero` and `payload` and on nothing that dispatches, so
 * every visit handler can import it without importing each other.
 */

import type { HeroId } from "../ids.js";
import { asArtifactDefId, asCreatureDefId, asSpellDefId } from "./ids.js";
import type { PrimarySkill, ResourceKind } from "../state.js";
import { RESOURCES } from "../state.js";
import type { Ctx } from "./ctx.js";
import { addResource, patchHero } from "./ctx.js";
import type { Payload } from "./payload.js";
import { payNum, payStr } from "./payload.js";
import {
  addCreatures, giveArtifact, grantExperience, hasFreeSlotFor, learnIllegality,
  learnSpell,
} from "./hero.js";

const PRIMARIES: readonly PrimarySkill[] =
  Object.freeze(["attack", "defense", "spellPower", "knowledge"]);

/** Hand over everything the payload promises. Silent about what it cannot give. */
export function grantReward(c: Ctx, heroId: HeroId, pay: Payload): void {
  const h = c.s.heroes[heroId];
  if (h === undefined) return;

  const gold = payNum(pay, "gold", 0);
  if (gold > 0) addResource(c, h.owner, "gold", gold);

  const resource = payStr(pay, "resource");
  const amount = payNum(pay, "amount", 0);
  if (resource !== "" && amount > 0 && RESOURCES.includes(resource as ResourceKind)) {
    addResource(c, h.owner, resource as ResourceKind, amount);
  }

  const art = payStr(pay, "artifact");
  if (art !== "" && c.content.artifacts.get(asArtifactDefId(art)) !== undefined) {
    giveArtifact(c, heroId, asArtifactDefId(art));
  }

  const rc = payStr(pay, "rewardCreature");
  const rn = payNum(pay, "rewardCount", 0);
  if (rc !== "" && rn > 0 && c.content.creatures.get(asCreatureDefId(rc)) !== undefined) {
    const army = c.s.armies[h.army];
    if (army !== undefined && hasFreeSlotFor(army, asCreatureDefId(rc))) {
      addCreatures(c, h.army, asCreatureDefId(rc), rn);
    }
  }

  const primary = payStr(pay, "primary");
  const primaryAmount = payNum(pay, "primaryAmount", 0);
  if (primaryAmount !== 0 && PRIMARIES.includes(primary as PrimarySkill)) {
    const cur = c.s.heroes[heroId];
    if (cur !== undefined) {
      const k = primary as PrimarySkill;
      patchHero(c, heroId, { primary: { ...cur.primary, [k]: cur.primary[k] + primaryAmount } });
    }
  }

  // The spell comes before the experience on purpose: experience can open a
  // level-up prompt, and a prompt swallows everything queued behind it.
  const spell = payStr(pay, "spell");
  if (spell !== "") {
    const id = asSpellDefId(spell);
    const cur = c.s.heroes[heroId];
    if (cur !== undefined && c.content.spells.get(id) !== undefined
        && learnIllegality(c, cur, id) === null) {
      learnSpell(c, heroId, id);
    }
  }

  const exp = payNum(pay, "experience", 0);
  if (exp > 0) grantExperience(c, heroId, exp);
}
